import { uniqBy } from "es-toolkit/compat";

import type { GetNodeSlotId, QueryEffectBuilds } from "@skilitics/nautilus-core-designer-data";

import type { ResolveActiveNodes } from "../../nodes/index.ts";
import { getNodeAncestorIdsCreate, type GetDesignerNodeById, type GetParentContextById } from "../../state/index.ts";
import type { DesignerNode, EffectBuildable, GetManifestByTypeId } from "../../types/index.ts";

type GetNodeEffectBuildablesOptions = {
  getManifest: GetManifestByTypeId;
  getParentContextById: GetParentContextById;
};

export const getNodeEffectBuildablesCreate =
  ({ getManifest, getParentContextById }: GetNodeEffectBuildablesOptions) =>
  (node: DesignerNode): EffectBuildable[] => {
    const { designer } = getManifest(node.typeId);
    const buildables =
      designer && designer.getEffectBuildables
        ? designer
            .getEffectBuildables(node, { parentContext: getParentContextById(node.id) })
            .filter((c): c is Exclude<typeof c, undefined | false> => !!c)
            .map(({ targetNodeId, ...buildable }) => ({
              nodeId: node.id,
              targetNodeId: targetNodeId ?? node.id,
              ...buildable,
            }))
        : [];
    // arguable prod only as it's kind of fundamental
    if (uniqBy(buildables, "name").length !== buildables.length) {
      // could specify which but so obvious to fix
      throw new Error(`Buildable names not unique on 'getEffectBuildables()' on Node Designer of type ${node.typeId}`);
    }
    return buildables;
  };

type ResolveEffectBuildablesOptions = {
  resolveActiveNodes: ResolveActiveNodes;
  getNodeEffectBuildables: GetNodeEffectBuildables;
  getById: GetDesignerNodeById;
  getSlotId: GetNodeSlotId;
  coreBuildables: ReadonlyArray<Omit<EffectBuildable, "nodeId">>;
};

export const resolveEffectBuildablesCreate = ({
  resolveActiveNodes,
  getNodeEffectBuildables,
  getById,
  getSlotId,
  coreBuildables,
}: ResolveEffectBuildablesOptions) => {
  const getNodeAncestorIds = getNodeAncestorIdsCreate({ getSlotId });

  return (nodeId: number, eventType: string): EffectBuildable[] => {
    const nodeEffectBuildables = resolveActiveNodes(nodeId).flatMap(getNodeEffectBuildables);
    const buildables = [...nodeEffectBuildables, ...coreBuildables.map((b) => ({ ...b, nodeId }))];

    const slotId = getSlotId(nodeId);
    const parentNode = slotId ? getById(slotId.parentId) : undefined;
    const node = getById(nodeId);
    const ancestorIds = getNodeAncestorIds(nodeId);
    return buildables.filter(
      ({ filter }) =>
        !filter ||
        filter({
          node: { typeId: node.typeId, role: node.role },
          parentNode: parentNode ? { typeId: parentNode.typeId, role: parentNode.role } : undefined,
          ancestorIds,
          eventType,
        }),
    );
  };
};

type ResolveAddableEffectBuildablesOptions = {
  resolveEffectBuildables: ResolveEffectBuildables;
  queryEffectBuilds: QueryEffectBuilds;
  getById: GetDesignerNodeById;
  getSlotId: GetNodeSlotId;
};

export const resolveAddableEffectBuildablesCreate =
  ({ resolveEffectBuildables, queryEffectBuilds, getById, getSlotId }: ResolveAddableEffectBuildablesOptions) =>
  (nodeOrNodeId: DesignerNode | number, eventType: string, responseId: number) => {
    const node = typeof nodeOrNodeId === "number" ? getById(nodeOrNodeId) : nodeOrNodeId;
    const effects = queryEffectBuilds({ type: "byResponseId", responseId });

    return resolveEffectBuildables(node.id, eventType)
      .filter((b) => !b.unique || !effects.find((e) => e.nodeId === b.nodeId && e.buildableName === b.name))
      .filter(({ restrictTo, nodeId }) => {
        switch (restrictTo) {
          case "self": {
            return node.id === nodeId;
          }
          case "parent": {
            const slotId = getSlotId(nodeId);
            return node.id === slotId?.parentId;
          }
          case "children": {
            const slotId = getSlotId(node.id);
            return slotId?.parentId === nodeId;
          }
          default:
            return true;
        }
      });
  };

export type GetNodeEffectBuildables = ReturnType<typeof getNodeEffectBuildablesCreate>;

export type ResolveEffectBuildables = ReturnType<typeof resolveEffectBuildablesCreate>;

export type ResolveAddableEffectBuildables = ReturnType<typeof resolveAddableEffectBuildablesCreate>;
